import {AnimatePresence, motion} from 'framer-motion';
import {DataStatus, FormControl} from 'platform/components';
import {
  Box,
  Center,
  getSize,
  Heading,
  HStack,
  Icon,
  Image,
  Space,
  Text,
  VStack,
} from 'platform/foundation';
import {useDateTimeFormatter} from 'platform/locale';
import styled from 'styled-components';

import {useCallback, useEffect, useState} from 'react';
import {useWatch} from 'react-hook-form';

import {isNil} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

import {useRenderCustomerMessageSmsTemplateMutation} from '@dms/api/core';
import i18n from '@dms/i18n';

import {
  DOT_CHARACTER,
  suffixTestId,
  TestIdProps,
  useBoolean,
  useDebouncedCallback,
} from 'shared/web';

import {handleApiError} from '../../../utils/handleApiError';
import imageUrl from '../assets/favicon.png';
import {FormValues} from '../types/FormValues';

interface SmsMessagePreviewProps extends TestIdProps {
  control: FormControl<FormValues>;
}

// SMS render ignores subject, but its still required in customer message BE args.
const SMS_SUBJECT = 'sms-subject';

const variants = {
  hidden: {
    opacity: 0,
    scaleY: 0,
    originY: 0,
  },
  visible: {
    opacity: 1,
    scaleY: 1,
    originY: 0,
  },
};

export const SmsMessagePreview = (props: SmsMessagePreviewProps) => {
  const formatDateTime = useDateTimeFormatter();
  const [body, customerId] = useWatch({control: props.control, name: ['body', 'customerId']});

  const [renderSms, {isLoading: isRendering, isError: isRenderingError}] =
    useRenderCustomerMessageSmsTemplateMutation();

  const [renderedBody, setRenderedBody] = useState<string | null>(null);

  const [wasFirstMessageLoaded, loadFirstMessage, hideTemplateBox] = useBoolean();

  const render = useCallback(() => {
    if (isNilOrEmpty(body) || isNil(customerId)) {
      hideTemplateBox();
      setRenderedBody(null);
      return;
    }

    renderSms({
      customerMessageId: 'customer-message',
      renderCustomerMessageRequestBody: {
        customerId,
        payload: {
          body,
          subject: SMS_SUBJECT,
        },
      },
    })
      .unwrap()
      .then((res) => setRenderedBody(res.body))
      .then(loadFirstMessage)
      .catch(handleApiError);
  }, [body, customerId, hideTemplateBox, loadFirstMessage, renderSms]);

  const handleRenderSms = useDebouncedCallback(render, 1000);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(handleRenderSms, [body, customerId]);

  const messageSentAtLabel = `SMS ${DOT_CHARACTER} ${i18n.t('general.labels.today')}, ${formatDateTime('timeShort', new Date())}`;
  const customerName = 'Omnetic';

  return (
    <Box
      minHeight={100}
      width="100%"
      height="100%"
      backgroundColor="palettes.neutral.20.100"
      borderRadius="large"
    >
      <Center height="100%">
        <VStack height="100%" justify="flex-end">
          <Space vertical={4} />

          <Heading size={4} align="center" data-testid={suffixTestId('smsMessagePreview', props)}>
            {i18n.t('general.labels.smsPreview')}
          </Heading>

          <Space vertical={8} />

          <PhonePreview>
            <DataStatus
              minHeight="100%"
              isLoading={isRendering}
              isError={isRenderingError}
              isEmpty={isNilOrEmpty(renderedBody)}
            >
              <HStack spacing={2}>
                <Box flex={1}>
                  <AnimatePresence mode="wait">
                    {wasFirstMessageLoaded && (
                      <motion.div
                        key="sms-message"
                        variants={variants}
                        initial="hidden"
                        animate="visible"
                        exit="hidden"
                        transition={{duration: 0.3, ease: 'easeInOut'}}
                      >
                        <Space vertical={1} />
                        <VStack spacing={1}>
                          <MessageBox>
                            <VStack spacing={1} maxHeight={100}>
                              <HStack justify="space-between">
                                <Icon value="action/settings" isNotFilled size={4} />
                                <Heading
                                  size={5}
                                  color="white"
                                  data-testid={suffixTestId('customerName', props)}
                                >
                                  {customerName}
                                </Heading>
                              </HStack>

                              <Box overflow="hidden" position="relative">
                                <Text
                                  size="small"
                                  color="white"
                                  data-testid={suffixTestId('messageBody', props)}
                                  overflowWrap="anywhere"
                                  preWrap
                                >
                                  {renderedBody}
                                </Text>
                              </Box>
                            </VStack>
                          </MessageBox>

                          <Text
                            size="xxSmall"
                            color="secondary"
                            data-testid={suffixTestId('messageSentAtLabel', props)}
                          >
                            {messageSentAtLabel}
                          </Text>
                        </VStack>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </Box>

                <Box
                  width={7}
                  height={7}
                  backgroundColor="palettes.neutral.20.100"
                  borderRadius="circular"
                >
                  <Center width="100%" height="100%">
                    <Image
                      width={5}
                      height={5}
                      src={imageUrl}
                      borderRadius="circular"
                      data-testid={suffixTestId('phoneIcon', props)}
                    />
                  </Center>
                </Box>
              </HStack>
            </DataStatus>
          </PhonePreview>
        </VStack>
      </Center>
    </Box>
  );
};

const PREVIEW_BOX_WIDTH = 95;
const PREVIEW_BOX_MIN_HEIGHT = 100;

// this custom pixel-perfect design taken from graphic team figma
// eslint-disable-next-line eag/no-css-property
const PhonePreview = styled.div`
  width: ${getSize(PREVIEW_BOX_WIDTH)};
  background-color: ${({theme}) => theme.colors.general.white};
  border: 10px solid ${({theme}) => theme.colors.palettes.neutral[800][100]};
  border-top-left-radius: 48px;
  border-top-right-radius: 48px;
  border-bottom: none;
  padding: ${getSize(7)};
  min-height: ${getSize(PREVIEW_BOX_MIN_HEIGHT)};
`;

const MessageBox = styled.div`
  padding: ${getSize(2)};
  background-color: ${({theme}) => theme.colors.palettes.neutral[800][100]};
  border-bottom-left-radius: ${({theme}) => theme.radii.medium};
  border-bottom-right-radius: ${({theme}) => theme.radii.medium};
  border-top-left-radius: ${({theme}) => theme.radii.medium};
  position: relative;
`;
